import React, { Component, Fragment, ReactNode } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import { Img } from "react-image";
import CalculatedPayment from "src/containers/CalculatedPayment";
import Msrp from "Shared/Msrp/index";
import styled, { css } from "styled-components";
import ArrowDownIcon from "src/components/icons/ArrowDown";
import ArrowLeftIcon from "src/components/icons/ArrowLeft";
import ArrowRightIcon from "src/components/icons/ArrowRight";
import GarageIcon from "src/components/icons/Garage";
import { Desktop, NotDesktop } from "src/components/shared/Responsive";
import VinNumber from "src/components/VinNumber/VinNumber";
import { EMPTY_VALUE_DASH } from "src/constants";
import PoweredByImage from "src/images/powered-by.inline.svg";
import { getApiUrl } from "src/store/dataService/routes/shared/osfetch";
import { formatNumberAsPercentage, localizeCurrencyToWholeNumber, localizeCurrencyWithDecimals } from "src/utils";
import { canShowMSRP, showStrikethroughForMSRP } from "src/utils/account";
import { getTradeInEstimate } from "src/utils/tradePending";
import { getOfferData, getPaymentData, getPaymentPrice, getSelectedPaymentType } from "src/utils/vehicles";
import { Accessory } from "Types/api/Accessory";
import {
    AccountState,
    DealState,
    LeaseOffer,
    LoanOffer,
    Offer,
    Payment,
    PaymentTypeOptions,
    VehicleState,
} from "Types/state";
import { TradeIn } from "Types/TradeIn";
import "./VehicleInfo.scss";

const IconStyle = css<{ affix: string }>`
    color: ${(props) => props.theme.palette.primary.main};
    display: inline-block;
    height: 13px;
    padding: ${(props) => (props.affix === "prefix" ? "0 4px 0 0" : "0 0 0 4px")};
    width: 11px;
`;
const ArrowDown = styled(ArrowDownIcon)<{ affix: string }>`
    ${IconStyle};
`;
const ArrowLeft = styled(ArrowLeftIcon)<{ affix: string }>`
    ${IconStyle};
`;
const ArrowRight = styled(ArrowRightIcon)<{ affix: string }>`
    ${IconStyle};
`;
const GarageButtonIcon = styled(GarageIcon)<{ affix: string }>`
    ${IconStyle};

    height: 22px; /* stylelint-disable-line */
    width: 27px;
`;

const TextLight = styled("span")`
    color: ${(props) => props.theme.palette.grey[20]};
    font-size: 13px;
    font-weight: normal;
`;

export const MSRPWithPrice = styled(TextLight)<{ showStrikethrough: boolean }>`
    text-decoration: ${(props) => (props.showStrikethrough ? "line-through" : "none")};
`;

export interface VehicleInfoProps {
    availableAccessories: Accessory[];
    account: AccountState;
    deal: DealState;
    onGoToGarage: () => void;
    onViewCustomizedPaymentPlan: () => void;
    tradePending: TradeIn;
    vehicle: VehicleState;
}

class VehicleInfo extends Component<VehicleInfoProps & WithTranslation> {
    public render() {
        const { account, deal, tradePending, vehicle, t } = this.props;
        const { title } = vehicle;
        const selectedPaymentName = getSelectedPaymentType(vehicle);
        const selectedPayment = getPaymentData(vehicle);
        const selectedOffer = getOfferData(vehicle);
        const firstName = (deal.person && deal.person.first_name) || "";
        const isTaxCustomerRequested = account.integration_settings?.Financing.calculate_taxes === "customer_requested";
        const detailsTitle = firstName ? (
            <Trans i18nKey="ContinueDeal.VehicleInfo.detailsTitle1">
                <b>
                    {{ firstName }}
                    's
                </b>{" "}
                {{ title }}
                <br />
            </Trans>
        ) : (
            <Trans i18nKey="ContinueDeal.VehicleInfo.detailsTitle2">
                <b>Your</b> {{ title }}
                <br />
            </Trans>
        );
        const vehicleDeal = {
            payments: vehicle.payments,
            selectedPaymentIndex: vehicle.payments?.selected,
            vin: vehicle.vin,
        } as any;

        return (
            <div className="VehicleInfo">
                <NotDesktop>
                    <button
                        className="VehicleInfo__button VehicleInfo__button--back-to-garage"
                        onClick={this.handleBackToGarageClick}
                    >
                        <ArrowLeft affix="prefix" />
                        {t("ContinueDeal.VehicleInfo.backButtonLabel")}
                    </button>
                </NotDesktop>
                <Desktop>
                    <div className="VehicleInfo__logos-container">
                        {account.logo_splash && (
                            <>
                                <img className="VehicleInfo__logo" src={account.logo_splash} alt={account.name} />
                                <div className="VehicleInfo__logo-separator" />
                            </>
                        )}
                        <PoweredByImage className="VehicleInfo__logo" />
                    </div>
                </Desktop>
                <div className="VehicleInfo__content">
                    <div className="VehicleInfo__image-container">
                        <img className="VehicleInfo__image" src={vehicle.thumbnail} alt="" />
                    </div>
                    <div className="VehicleInfo__details-container">
                        <p className="VehicleInfo__details">
                            {detailsTitle}
                            <VinNumber>{vehicle.vin}</VinNumber>
                        </p>
                        <NotDesktop>
                            <Msrp vehicle={vehicle} />
                            <div className="VehicleInfo__price-container">
                                <CalculatedPayment
                                    account={account}
                                    {...vehicleDeal}
                                    className="VehicleInfo__price"
                                    inGarage={true}
                                    displayPaymentInfo={!isTaxCustomerRequested}
                                />
                            </div>
                        </NotDesktop>
                        <Desktop>{this.renderVehicleColorInfo()}</Desktop>
                        <NotDesktop>
                            <button
                                className="VehicleInfo__button VehicleInfo__button--bold VehicleInfo__button--view-payment-plan"
                                onClick={this.handleViewCustomizedPaymentPlanClick}
                            >
                                {t("ContinueDeal.VehicleInfo.viewPlanButtonLabel")}
                                <ArrowDown affix="suffix" />
                            </button>
                        </NotDesktop>
                    </div>
                </div>
                <Desktop>
                    <div className="VehicleInfo__expanded-content">
                        <div className="VehicleInfo__expanded-content-header">
                            <button
                                className="VehicleInfo__button VehicleInfo__button--bold VehicleInfo__button--view-payment-plan"
                                onClick={this.handleViewCustomizedPaymentPlanClick}
                            >
                                {t("ContinueDeal.VehicleInfo.viewPlanButtonLabel")}
                                <ArrowRight affix="suffix" />
                            </button>
                            <div className="VehicleInfo__price-container">
                                <CalculatedPayment
                                    {...vehicleDeal}
                                    className="VehicleInfo__price"
                                    inGarage={true}
                                    displayPaymentInfo={!isTaxCustomerRequested}
                                />
                            </div>
                        </div>
                        <ul className="VehicleInfo__expanded-content-list">
                            {this.renderPaymentPlan(selectedPaymentName, selectedPayment, selectedOffer, tradePending)}
                        </ul>
                        <div className="VehicleInfo__expanded-content-footer">
                            <button
                                className="VehicleInfo__button VehicleInfo__button--bold VehicleInfo__button--back-to-garage"
                                onClick={this.handleBackToGarageClick}
                            >
                                <GarageButtonIcon affix="prefix" />
                                {t("ContinueDeal.VehicleInfo.backButtonLabel")}
                            </button>
                        </div>
                    </div>
                </Desktop>
            </div>
        );
    }

    private renderPaymentPlan(
        selectedPaymentName: PaymentTypeOptions,
        selectedPayment: Payment | null,
        selectedOffer: Offer | null,
        tradePending: TradeIn
    ) {
        const paymentPlan = this.getPaymentPlan(selectedPaymentName, selectedPayment, selectedOffer, tradePending);
        return paymentPlan.map((feature) => (
            <li className="VehicleInfo__expanded-content-list-item" key={feature.label}>
                <span className="VehicleInfo__expanded-content-list-item-label">{feature.label}</span>
                <span className="VehicleInfo__expanded-content-list-item-value">{feature.value}</span>
            </li>
        ));
    }

    private renderVehicleColorInfo() {
        // TODO uncomment when vehicle color data be retrieved from API
        /* return (
            <ul className="VehicleInfo__colors">
                <li className="VehicleInfo__color">
                    <span className="VehicleInfo__color-pin VehicleInfo__color-pin--grey" />
                    <span className="VehicleInfo__color-label">
                        Exterior: Silver Ice Metallic
                    </span>
                </li>
                <li className="VehicleInfo__color">
                    <span className="VehicleInfo__color-pin VehicleInfo__color-pin--black" />
                    <span className="VehicleInfo__color-label">
                        Interior: Black
                    </span>
                </li>
            </ul>
        ); */
        return "";
    }

    private getPaymentPlan = (
        selectedPaymentName: PaymentTypeOptions,
        selectedPayment: Payment | null,
        selectedOffer: Offer | null,
        tradePending: TradeIn
    ) => {
        const { vehicle, t, account } = this.props;

        interface Item {
            label: string;
            value: React.ReactNode;
        }

        const paymentPlan: Item[] = [
            {
                label: t("ContinueDeal.VehicleInfo.paymentTypeLabel"),
                value: this.getPaymentTypeDisplayValue(selectedPaymentName),
            },
        ];

        if (selectedPaymentName !== "cash") {
            // @ts-ignore SelectedPayment.Type will never be of type Cash due to above.
            const term = selectedPayment ? selectedPayment.term : "";
            const termValue = term ? `${term} ${t("ContinueDeal.VehicleInfo.months")}` : EMPTY_VALUE_DASH;

            // @ts-ignore Selected Payment will never be of type "Cash" here
            const downPaymentValue = localizeCurrencyToWholeNumber(selectedPayment && selectedPayment.down_payment);
            const classPrefix = selectedPaymentName === "lease" ? "lease" : "others";
            const downPaymentLabel = t(`ContinueDeal.VehicleInfo.${classPrefix}DownPaymentLabel`);
            [
                {
                    label: t("ContinueDeal.VehicleInfo.termLabel"),
                    value: termValue,
                },
                {
                    label:
                        (selectedPaymentName === "lease"
                            ? account.account_disclaimers?.down_payment_label_lease
                            : account.account_disclaimers?.down_payment_label_loan) ?? downPaymentLabel,
                    value: downPaymentValue,
                },
                {
                    label: t("ContinueDeal.VehicleInfo.bankLabel"),
                    value: this.getBankRateValue(selectedOffer, selectedPaymentName),
                },
            ].forEach((f) => paymentPlan.push(f));
        }

        const tradeInEstimate = getTradeInEstimate(tradePending);

        [
            {
                label: t("ContinueDeal.VehicleInfo.tradeInLabel"),
                value: tradeInEstimate ? localizeCurrencyToWholeNumber(tradeInEstimate) : EMPTY_VALUE_DASH,
            },
            {
                label: t("ContinueDeal.VehicleInfo.incentivesLabel"),
                value: this.getIncentivesSummary(selectedOffer),
            },
            // {
            //     label: "Protection",
            //     value: this.getProtectionSummary()
            // },
        ].forEach((f) => paymentPlan.push(f));

        const price = getPaymentPrice(vehicle);

        if (!canShowMSRP(account) || !vehicle.msrp || vehicle.msrp !== price) {
            paymentPlan.unshift({
                label: account.account_disclaimers.base_price,
                value: localizeCurrencyToWholeNumber(price),
            });
        }

        if (canShowMSRP(account) && vehicle.msrp) {
            paymentPlan.unshift({
                label: t("ContinueDeal.VehicleInfo.msrpLabel"),
                value:
                    vehicle.msrp === price ? (
                        <span>{localizeCurrencyToWholeNumber(vehicle.msrp)}</span>
                    ) : (
                        <MSRPWithPrice showStrikethrough={showStrikethroughForMSRP(account)}>
                            {localizeCurrencyToWholeNumber(vehicle.msrp)}
                        </MSRPWithPrice>
                    ),
            });
        }

        const { availableAccessories = [] } = this.props;

        if (availableAccessories.length) {
            paymentPlan.push({
                label: account.account_disclaimers.upgrades_label,
                value: this.getAccessoriesSummary(),
            });
        }

        if (selectedOffer?.fees?.length) {
            paymentPlan.push({
                label: t("ContinueDeal.VehicleInfo.feesLabel"),
                value: this.getTotal(selectedOffer.fees),
            });
        }

        if (selectedOffer?.taxes?.length) {
            const notMonthlyTaxes = selectedOffer.taxes.filter((tax) => tax.collected !== "monthly");
            const monthlyTaxes = selectedOffer.taxes.filter((tax) => tax.collected === "monthly");

            if (notMonthlyTaxes.length) {
                paymentPlan.push({
                    label: t("ContinueDeal.VehicleInfo.taxesLabel"),
                    value: this.getTotal(notMonthlyTaxes),
                });
            }

            if (monthlyTaxes.length) {
                paymentPlan.push({
                    label: t("ContinueDeal.VehicleInfo.monthlySalesLabel"),
                    value: t("ContinueDeal.VehicleInfo.monthlySalesSummary", {
                        summary: this.getTotal(monthlyTaxes),
                    }),
                });
            }
        }
        return paymentPlan;
    };

    private getTotal = (items: Array<{ name: string; value: number }>): string =>
        localizeCurrencyWithDecimals(items.reduce((acc: number, fee) => acc + (fee.value ?? 0), 0));

    private getBankRateValue = (selectedOffer: Offer | null, selectedPaymentName: PaymentTypeOptions): ReactNode => {
        const offer = selectedOffer as LoanOffer | LeaseOffer;
        const loanOffer = offer as LoanOffer;
        return selectedPaymentName !== "cash" && offer && offer.lender ? (
            <Fragment>
                {loanOffer && formatNumberAsPercentage(loanOffer.interest_rate)}
                <Img
                    src={[getApiUrl(`/banks/${offer.lender.toLowerCase().replace(" ", "-")}/logo`)]}
                    unloader={<div>{offer.lender}</div>}
                />
            </Fragment>
        ) : (
            EMPTY_VALUE_DASH
        );
    };

    private getPaymentTypeDisplayValue = (selectedPaymentName: PaymentTypeOptions) => {
        const { t } = this.props;
        const paymentTypes = {
            cash: t("ContinueDeal.VehicleInfo.cash"),
            lease: t("ContinueDeal.VehicleInfo.lease"),
            loan: t("ContinueDeal.VehicleInfo.finance"),
        };

        return selectedPaymentName ? paymentTypes[selectedPaymentName] : "";
    };

    private getIncentivesSummary = (offer: Offer | null) => {
        const incentives = (offer && offer.rebates) || [];
        let incentivesTotal = 0;

        incentives.forEach((incentive: any) => (incentivesTotal += incentive.value));

        return incentivesTotal ? localizeCurrencyWithDecimals(incentivesTotal) : EMPTY_VALUE_DASH;
    };

    private getAccessoriesSummary = () => {
        const { accessories = [] } = this.props.vehicle;
        const accessoriesTotal = accessories.reduce((carry: number, a: Accessory) => carry + (a.price ?? 0), 0);

        return accessoriesTotal ? localizeCurrencyToWholeNumber(accessoriesTotal) : EMPTY_VALUE_DASH;
    };

    /* private getProtectionSummary = () => {
        // TODO update method when protection be retrieved by API
        const vehicleProtections: Accessory[] = [];
        let protectionsTotal = 0;

        vehicleProtections.forEach(
            protection => (protectionsTotal += protection.price)
        );

        return protectionsTotal ? localizeCurrencyToWholeNumber(protectionsTotal) : EMPTY_VALUE_DASH;
    }; */

    private handleBackToGarageClick = () => {
        this.props.onGoToGarage();
    };

    private handleViewCustomizedPaymentPlanClick = () => {
        this.props.onViewCustomizedPaymentPlan();
    };
}

export default withTranslation()(VehicleInfo);
